/**
 * Zustand store for Optimizations page tab state.
 * Lifted from local component state in OptimizationsPage so it can be URL-synced
 * via useUrlSync (/optimizations/grid-search and /optimizations/walk-forward).
 *
 * Also holds selectedOptimizationId so drill-in detail views are URL-navigable:
 *   /optimizations/grid-search/:optimizationId
 *   /optimizations/walk-forward/:optimizationId
 */

import { create } from 'zustand';

export type OptimizationsTab = 'grid_search' | 'walk_forward';

interface OptimizationsPageStore {
  activeTab: OptimizationsTab;
  setActiveTab: (tab: OptimizationsTab) => void;
  /** The optimization run currently open in detail view, or null for list view. */
  selectedOptimizationId: string | null;
  setSelectedOptimizationId: (id: string | null) => void;
  /**
   * SHA-23: set when the current URL could not be parsed into a valid
   * /optimizations route (e.g. a bare `/optimizations/<runId>` deep link
   * missing the tab slug). The page renders a graceful not-found panel
   * instead of the router throwing and white-screening the app.
   * Holds the raw offending pathname, or null when the route is valid.
   */
  routeNotFoundPath: string | null;
  setRouteNotFoundPath: (path: string | null) => void;
  /**
   * Reset to the default (logged-out) state. Called on logout/login (SHA-71)
   * so a previously-selected optimization run id doesn't leak into the next
   * account's session.
   */
  reset: () => void;
}

export const useOptimizationsPageStore = create<OptimizationsPageStore>((set) => ({
  activeTab: 'grid_search',
  setActiveTab: (tab) => set({ activeTab: tab }),
  selectedOptimizationId: null,
  setSelectedOptimizationId: (id) => set({ selectedOptimizationId: id }),
  routeNotFoundPath: null,
  setRouteNotFoundPath: (path) => set({ routeNotFoundPath: path }),
  reset: () => set({ activeTab: 'grid_search', selectedOptimizationId: null, routeNotFoundPath: null }),
}));
